import { Link, useLocation } from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import * as React from "react";

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  findNav,
  primaryNav,
  settingsNav,
  tertiaryNav,
  type NavEntry,
  type NavItem,
  type NavSectionLink,
} from "@/constants/nav.constants";
import { cn } from "@/lib/utils";

// Flat pill nav. No inline collapsibles: a parent with children is a single
// pill that navigates into its route space, and the sidebar swaps to a drilled
// sub-view (see AppSidebar) listing the children. The sidebar surface is
// transparent over the page wash, so a solid `bg-sidebar-accent` hover would
// read as a hard opaque patch. Light mode lifts the pill instead: hover is a
// half-strength `bg-background`, the active item is the full surface with a
// hairline shadow, and labels sit at full `sidebar-foreground` so they hold up
// against the tinted wash. Dark mode has no lighter surface to lift to, so it
// keeps a foreground wash and the softer label.
//
// The active styling is driven off the `data-status="active"` attribute
// TanStack Router sets on the link, baked straight into this class string
// (not passed via the router's `activeProps`). Two reasons:
//  1. `activeProps` is concatenated onto the anchor AFTER this class WITHOUT
//     tailwind-merge, so a base `text-sidebar-foreground/70` here would out-order
//     an `activeProps` `text-foreground` and the active label would stay muted.
//     A `data-[status=active]:` variant carries an attribute selector, so it
//     always wins on specificity regardless of source order.
//  2. The same goes for the `dark:` pair: its selector is more specific than
//     the light rule it replaces, so order in this string does not matter.
//
// Nothing centers the collapsed pill. The group's padding is what collapses
// (px-5 -> px-3), the pill sits at the start of the row in both states, and
// 0.75rem of inset is exactly what leaves the 2.25rem pill centered in the
// 3.75rem rail. So the icon's x is just `group padding + pill padding`, two
// linear values, and it slides in a straight line.
//
// Do NOT reintroduce `justify-center` (or `mx-auto`) on the item to center the
// pill. That was the zig-zag:
//
//  - The pill's width interpolates from `width: 100%` to `2.25rem` while its
//    container is itself shrinking, so its used width is
//    `calc(100% * (1 - p) + 36px * p)` and the centering offset
//    `(container - pill) / 2` works out to the parabola `106p - 102p²`. Measured,
//    the icon left ran 36px -> 53px -> 20px: right, then back left, mid-collapse.
//  - `mx-auto` is worse: `auto` margins can't be CSS-transitioned at all, so the
//    pill snaps to center the instant collapse starts, ahead of the panel.
//
// `size-9!` rather than `h-9!`, because the SidebarMenuButton primitive pins
// `group-data-[collapsible=icon]:size-8!`. `size-9` replaces it outright
// (tailwind-merge treats them as the same group), whereas `h-9` only wins the
// height and leaves the width pinned at 2rem, which makes a 32x36 pill whose
// 1.25rem icon overflows the 1rem content box and gets cut off by the button's
// own `overflow-hidden`.
//
// `duration-200 ease-linear` matches the panel, the sidebar gap and this group's
// padding, which all run `linear` over 200ms. Tailwind's
// `transition-[width,height,padding]` on the primitive otherwise defaults to
// `cubic-bezier(0.4, 0, 0.2, 1)`, and mixing curves warps the same motion even
// when nothing else is wrong.
//
// The `active:` overrides matter: the SidebarMenuButton primitive bakes in
// `active:bg-sidebar-accent active:text-sidebar-accent-foreground` (the CSS
// :active pseudo, fired while the pointer is held down). Left alone, clicking a
// pill flashes the opaque accent fill for the duration of the press before the
// route-active `data-[status=active]` wash settles in, which reads as a weird
// primary-colored blink on the glass. Pin the press state to the same
// foreground wash so the click feels continuous.
const ITEM_CLASS =
  "relative h-8 gap-2.5 rounded-md px-4 text-[13px] in-data-[mobile=true]:h-11 in-data-[mobile=true]:text-[15px] in-data-[mobile=true]:[&>svg]:size-[18px] font-medium text-sidebar-foreground hover:bg-background/50 hover:text-foreground active:bg-background/70 active:text-foreground data-[status=active]:bg-background data-[status=active]:text-foreground data-[status=active]:shadow-xs data-[status=active]:hover:bg-background dark:text-sidebar-foreground/70 dark:hover:text-foreground dark:hover:bg-foreground/8 dark:data-[status=active]:text-foreground dark:active:bg-foreground/12 dark:data-[status=active]:bg-foreground/12 dark:data-[status=active]:ring-1 dark:data-[status=active]:ring-foreground/15 dark:data-[status=active]:hover:bg-foreground/14 [&>svg]:size-3.5 [&>svg]:transition-[width,height] [&>svg]:duration-200 [&>svg]:ease-linear duration-200 ease-linear group-data-[collapsible=icon]:size-9! group-data-[collapsible=icon]:[&>svg]:size-[18px]";

// px-3 is not arbitrary: (3.75rem rail - 2.25rem pill) / 2. It's what centers the
// collapsed pill without any centering rule. Same easing as the panel width so the
// inset and the panel finish together.
const GROUP_CLASS =
  "gap-1 px-5 in-data-[mobile=true]:px-3 transition-[padding] duration-200 ease-linear group-data-[collapsible=icon]:px-3";
// Collapsed, the label fades out and pulls itself up by its own height (`-mt-8` in the ui
// sidebar), which parks an invisible box on top of the row above it. Without
// `pointer-events-none` that box eats the hover and the click of whatever sits there: the "back"
// pill of a drilled sub-view lost both.
const LABEL_CLASS =
  "px-3 text-[11px] font-medium tracking-wider text-sidebar-foreground/50 group-data-[collapsible=icon]:pointer-events-none";
const EXACT_ACTIVE_OPTIONS = { exact: true } as const;
const PREFIX_ACTIVE_OPTIONS = { exact: false } as const;

function isNavLink(entry: NavEntry): entry is NavSectionLink {
  return "to" in entry;
}

function hasChildren(entry: NavEntry): boolean {
  return entry.items.length > 0;
}

/** Routes that belong to an entry's route space (the parent itself plus children). */
function entryRoutes(entry: NavEntry): string[] {
  const routes: string[] = [];
  if (isNavLink(entry)) routes.push(entry.to);
  for (const item of entry.items) routes.push(item.to);
  return routes;
}

/** Whether `pathname` is `route` itself or a page under it. "/" only covers itself, or it would cover every page. */
function routeCovers(route: string, pathname: string): boolean {
  return pathname === route || (route !== "/" && pathname.startsWith(`${route}/`));
}

/**
 * Finds the nav entry whose route space covers `pathname`, preferring the most
 * specific (longest matching route) so a nested parent wins over a broader one.
 * Only entries with children are considered: leaf entries don't drill in.
 */
export function findActiveParent(entries: NavEntry[], pathname: string): NavEntry | null {
  let best: NavEntry | null = null;
  let bestLen = -1;

  for (const entry of entries) {
    if (!hasChildren(entry)) continue;
    for (const route of entryRoutes(entry)) {
      if (routeCovers(route, pathname)) {
        if (route.length > bestLen) {
          best = entry;
          bestLen = route.length;
        }
      }
    }
  }

  return best;
}

const ALL_NAV_ROUTES = [...primaryNav, ...findNav, ...tertiaryNav, ...settingsNav].flatMap(entryRoutes);

/**
 * The one nav route the current page belongs to: the longest route that is the URL itself or a
 * parent of it. A detail page (/clients/abc/plans) keeps its list pill (/clients) lit, while
 * /booking/requests lights its own pill and not the /booking one above it.
 */
function useActiveNavRoute(): string | null {
  const { pathname } = useLocation();

  return React.useMemo(() => {
    let best: string | null = null;
    for (const route of ALL_NAV_ROUTES) {
      if (routeCovers(route, pathname) && route.length > (best?.length ?? -1)) best = route;
    }
    return best;
  }, [pathname]);
}

// Every pill matches exactly, except the one that owns the current page: it matches its whole
// subtree so nested pages keep it lit. Picking that one up front is what stops a broad route
// (/booking) lighting up alongside a narrower sibling (/booking/requests).
function activeOptionsFor(to: string, activeRoute: string | null) {
  return to === activeRoute ? PREFIX_ACTIVE_OPTIONS : EXACT_ACTIVE_OPTIONS;
}

function NavEntryItem({ entry }: { entry: NavEntry }) {
  const activeRoute = useActiveNavRoute();
  const link = isNavLink(entry) ? entry : null;
  const draggable = hasChildren(entry);
  // Sections without a top-level `to` fall back to their first child so the row
  // is still clickable. Parents navigate into their route space; the sidebar
  // then swaps to the drilled sub-view (driven by the URL).
  const to = link?.to ?? entry.items[0]?.to;
  const params = link?.params ?? entry.items[0]?.params;

  return (
    <SidebarMenuItem className="flex">
      <SidebarMenuButton asChild tooltip={entry.label} className={ITEM_CLASS}>
        {to ? (
          <Link
            to={to}
            {...(params && { params })}
            activeOptions={activeOptionsFor(to, activeRoute)}
          >
            <entry.icon />
            <span>{entry.label}</span>
            {entry.badge ? <entry.badge /> : null}
            {draggable ? <ChevronRight className="ml-auto size-4 text-sidebar-foreground/40" /> : null}
          </Link>
        ) : (
          <span>
            <entry.icon />
            <span>{entry.label}</span>
          </span>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

type NavGroupProps = {
  /** Optional section header shown above the items. */
  label?: string;
  entries: NavEntry[];
  /** Extra classes for the group wrapper (e.g. `mt-auto` to pin it to the bottom). */
  className?: string;
};

/** A labelled block of pill nav items. Renders nothing when there are no entries. */
export function NavGroup({ label, entries, className }: NavGroupProps) {
  if (entries.length === 0) return null;

  return (
    <SidebarGroup className={cn(GROUP_CLASS, className)}>
      {label ? <SidebarGroupLabel className={LABEL_CLASS}>{label}</SidebarGroupLabel> : null}
      <SidebarMenu>
        {entries.map((entry) => (
          <NavEntryItem key={entry.label} entry={entry} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

type NavSubViewProps = {
  /** The parent entry label, shown as the section header. */
  label: string;
  items: NavItem[];
  /** Where the "back" row leads: the route that returns the sidebar to the main menu. */
  backTo: string;
  backLabel: string;
};

/**
 * The drilled sub-view: lists a parent's children as leaf pills under the
 * parent's name. Only the child that owns the current URL lights up, not
 * siblings that share a route prefix (e.g. the Overview child at /analytics
 * when on /analytics/usage). Renders nothing when there are no items.
 */
export function NavSubView({ label, items, backTo, backLabel }: NavSubViewProps) {
  const activeRoute = useActiveNavRoute();

  if (items.length === 0) return null;

  return (
    <SidebarGroup className={GROUP_CLASS}>
      {/* Sits under the logo instead of replacing it, so the brand stays put on
        every page. A plain pill keeps it centered in the collapsed rail too. */}
      <SidebarMenu className="mb-2">
        <SidebarMenuItem className="flex">
          <SidebarMenuButton asChild tooltip={backLabel} className={ITEM_CLASS}>
            <Link to={backTo}>
              <ArrowLeft />
              <span>{backLabel}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <SidebarGroupLabel className={LABEL_CLASS}>{label}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.to} className="flex">
            <SidebarMenuButton asChild tooltip={item.label} className={ITEM_CLASS}>
              <Link
                to={item.to}
                {...(item.params && { params: item.params })}
                activeOptions={activeOptionsFor(item.to, activeRoute)}
              >
                <item.icon />
                <span>{item.label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
